/**
 * Representation of a point in a two dimensional space
 *
 *     var point = new Ofcs.Point(125.456, 658.26645);
 */
export default class Point {
    /**
     * The x coordinate
     * @property {number} x
     */
    x = null;

    /**
     * The y coordinate
     * @property {number} y
     */
    y = null;

    /**
     * @param {number|Array} Float for x or array with x and y
     * @param {number} Optional param with y position
     */
    constructor(...args) {
        if (args.length === 2) this.setValues(...args);
        else if (args.length === 1 && Array.isArray(args[0])) this.setValues(...args[0]);
        else throw new Error('Point expects two floats or an array of two floats');
    }

    /**
     * Checks if two points are equal
     *
     * @param {Point} other Point to check against
     * @param {number} [delta=0.0001] Delta which defines the check tolerance
     * @return {boolean}
     */
    equal(other, delta = 0.0001) {
        return Math.abs(this.x - other.x) < delta && Math.abs(this.y - other.y) < delta;
    }

    /**
     * Internal function to check values before set them
     *
     * @param {number} x
     * @param {number} y
     */
    setValues(x, y) {
        this.x = parseFloat(x);
        this.y = parseFloat(y);

        if (Number.isNaN(this.x) || Number.isNaN(this.y)) {
            throw new Error('Invalid argument');
        }
    }

    /**
     * Transforms the point by the given transform matrix
     *
     * @param {Ofcs.Matrix} matrix Transform matrix
     * @return {Point}
     * @chainable
     */
    transform(matrix) {
        const oldX = this.x;
        const oldY = this.y;

        this.x = matrix.x(oldX, oldY);
        this.y = matrix.y(oldX, oldY);

        return this;
    }

    /**
     * Returns angle between this point and the given point
     *
     * @param {Point} point Point to calculate the angle for
     * @return {number} Angle between points
     */
    getAngleTo(point) {
        const x = this.x - point.x;
        const y = this.y - point.y;

        if (!x && !y) {
            return 0;
        }

        return (((Math.atan2(-y, -x) * 180) / Math.PI) + 360) % 360;
    }

    /**
     * Returns distance between this point and the given point
     *
     * @param {Point} point Point to calculate the distance
     * @return {number} Distance between points
     */
    getDistanceTo(point) {
        const distX = this.x - point.x;
        const distY = this.y - point.y;

        return Math.sqrt((distX ** 2) + (distY ** 2));
    }

    /**
     * Calculate distance from a given point to a given line
     * @param {Ofcs.Line} line
     * @return {number}
     */
    getDistanceToLine(line) {
        const Dx = line.endPoint.x - line.startPoint.x;
        const Dy = line.endPoint.y - line.startPoint.y;
        const interim1 = (Dy * this.x) - (Dx * this.y) - (line.startPoint.x * line.endPoint.y);
        const interim2 = interim1 + (line.endPoint.x * line.startPoint.y);
        return Math.abs(interim2) / Math.sqrt((Dx ** 2) + (Dy ** 2));
    }

    /**
     * Return an array containing x and y
     *
     * This can be used to serialize and persist the point.
     * The name of this method is a bit confusing, because it
     * conforms to JavaScript's JSON API.
     *
     * @link https://developer.mozilla.org/en-US/docs/JSON#toJSON()_method
     * @return {Array} Array with x and y
     */
    toJSON() {
        return [this.x, this.y];
    }

    /**
     * Return a cloned instance of Ofcs.Point
     *
     * @return {Point}
     */
    clone() {
        return new Point(this.x, this.y);
    }

    /**
     * Move the point by a distance.
     *
     * @param {Ofcs.Distance} distance
     *
     * @return {Point}
     * @chainable
     */
    move(distance) {
        this.x += distance.point.x;
        this.y += distance.point.y;

        return this;
    }

    /**
     * Create a new point in the given distance for the given angle
     *
     * @param {number} angle
     * @param {number} [distance=10]
     * @return {Point}
     */
    moveByAngle(angle, distance = 10) {
        return new Point(
            (Math.cos((angle * Math.PI) / 180) * distance) + this.x,
            (Math.sin((angle * Math.PI) / 180) * distance) + this.y,
        );
    }
}
